import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, type Control, type FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { useAuthStore } from '@/auth/store';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { VesselSelect } from '@/components/ui/vessel-select';
import {
  useCreateDrydockCampaign,
  useDrydockCampaign,
  useUpdateDrydockCampaign,
} from '@/features/drydock-campaigns/queries';
import type {
  CreateDrydockCampaignInput,
  UpdateDrydockCampaignInput,
} from '@/features/drydock-campaigns/schema';
import type { DrydockCampaign } from '@/features/drydock-campaigns/types';
import { ApiError } from '@/lib/api';

/**
 * Flat form schema. The wire payloads are split (Create has
 * companyId/vesselId/startDate as mandatory and immutable, Update is
 * a strict subset of the rest), but react-hook-form is friendlier
 * with one flat shape — we filter to the right subset at submit time.
 *
 * Currency is uppercased on submit; numeric budget arrives as a
 * string from the input and is coerced + validated as ≥ 0.
 */
const isoDateString = z
  .string()
  .min(1, 'Required')
  .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'Invalid date' });

const optionalIsoDateString = z
  .string()
  .optional()
  .or(z.literal('').transform(() => undefined))
  .refine((v) => v === undefined || !Number.isNaN(Date.parse(v)), {
    message: 'Invalid date',
  });

/**
 * Empty-string-to-undefined preprocessor. We can't chain
 * `.optional().or(literal(''))` because zod's union resolution still
 * passes the empty string into the downstream `.refine`, which then
 * trips on regex/numeric checks meant for actually-filled values.
 * `z.preprocess` runs before any inner schema, so we get a clean
 * undefined-or-real-value boundary.
 */
const emptyToUndef = (v: unknown): unknown => {
  if (typeof v !== 'string') return v;
  const trimmed = v.trim();
  return trimmed === '' ? undefined : trimmed;
};

const optionalString = z.preprocess(emptyToUndef, z.string().optional());

const optionalCurrency = z.preprocess(
  emptyToUndef,
  z
    .string()
    .regex(/^[A-Za-z]{3}$/, { message: 'invalidCurrency' })
    .optional(),
);

const optionalNumberString = z.preprocess(
  emptyToUndef,
  z
    .string()
    .refine(
      (v) => {
        const n = Number(v);
        return Number.isFinite(n) && n >= 0;
      },
      { message: 'Invalid amount' },
    )
    .optional(),
);

const formSchema = z
  .object({
    vesselId: z.string().uuid(),
    title: z.string().trim().min(1, 'Required').max(300),
    description: optionalString,
    startDate: isoDateString,
    endDate: optionalIsoDateString,
    location: optionalString,
    shipyard: optionalString,
    totalBudgetAmount: optionalNumberString,
    totalBudgetCurrency: optionalCurrency,
    notes: optionalString,
  })
  .superRefine((data, ctx) => {
    if (data.endDate && data.startDate) {
      const s = Date.parse(data.startDate);
      const e = Date.parse(data.endDate);
      if (Number.isFinite(s) && Number.isFinite(e) && e <= s) {
        ctx.addIssue({
          code: 'custom',
          path: ['endDate'],
          message: 'End date must be after start date',
        });
      }
    }
    if (
      (data.totalBudgetAmount && !data.totalBudgetCurrency) ||
      (!data.totalBudgetAmount && data.totalBudgetCurrency)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['totalBudgetCurrency'],
        message: 'Currency and amount must be set together',
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

function TextField({
  control,
  name,
  label,
  hint,
  type = 'text',
  textarea = false,
  placeholder,
  disabled = false,
}: {
  control: Control<FormValues>;
  name: FieldPath<FormValues>;
  label: string;
  hint?: string;
  type?: 'text' | 'date' | 'number';
  textarea?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {textarea ? (
              <textarea
                {...field}
                rows={3}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
                placeholder={placeholder}
                disabled={disabled}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-invalid={Boolean(fieldState.error)}
              />
            ) : (
              <Input
                type={type}
                {...field}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
                placeholder={placeholder}
                disabled={disabled}
                aria-invalid={Boolean(fieldState.error)}
                step={type === 'number' ? '0.01' : undefined}
                min={type === 'number' ? '0' : undefined}
              />
            )}
          </FormControl>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function defaultsFromExisting(c: DrydockCampaign): FormValues {
  return {
    vesselId: c.vesselId,
    title: c.title,
    description: c.description ?? '',
    startDate: isoToDateInput(c.startDate),
    endDate: isoToDateInput(c.endDate),
    location: c.location ?? '',
    shipyard: c.shipyard ?? '',
    totalBudgetAmount: c.totalBudgetAmount ?? '',
    totalBudgetCurrency: c.totalBudgetCurrency ?? '',
    notes: c.notes ?? '',
  };
}

function emptyDefaults(): FormValues {
  return {
    vesselId: '',
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    location: '',
    shipyard: '',
    totalBudgetAmount: '',
    totalBudgetCurrency: '',
    notes: '',
  };
}

interface FormBodyProps {
  mode: 'create' | 'edit';
  initialValues: FormValues;
  submitting: boolean;
  errorMessage: string | null;
  onSubmit: (values: FormValues) => void;
}

function FormBody({
  mode,
  initialValues,
  submitting,
  errorMessage,
  onSubmit,
}: FormBodyProps) {
  const { t } = useTranslation();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });

  // Keep the form in sync when the existing campaign loads in edit
  // mode (the parent renders FormBody before the GET resolves to
  // avoid a flash of empty fields).
  useEffect(() => {
    form.reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues.vesselId, initialValues.title, initialValues.startDate]);

  const handleSubmit = form.handleSubmit((values) => {
    onSubmit(values);
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {errorMessage && (
          <Alert variant="destructive">
            <AlertTitle>{t('drydockCampaign.form.saveError')}</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('drydockCampaign.form.sectionContext')}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="vesselId"
              render={({ field, fieldState }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>{t('drydockCampaign.fields.vessel')}</FormLabel>
                  <FormControl>
                    <VesselSelect
                      value={field.value || null}
                      onChange={(v) => field.onChange(v ?? '')}
                      allowEmpty
                      emptyLabel={t('drydockCampaign.form.vesselPlaceholder')}
                      disabled={mode === 'edit'}
                      aria-invalid={Boolean(fieldState.error)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="sm:col-span-2">
              <TextField
                control={form.control}
                name="title"
                label={t('drydockCampaign.fields.title')}
              />
            </div>
            <TextField
              control={form.control}
              name="startDate"
              label={t('drydockCampaign.fields.startDate')}
              type="date"
              hint={
                mode === 'edit'
                  ? t('drydockCampaign.form.startDateImmutableHint')
                  : undefined
              }
              disabled={mode === 'edit'}
            />
            <TextField
              control={form.control}
              name="endDate"
              label={t('drydockCampaign.fields.endDate')}
              type="date"
              hint={t('drydockCampaign.form.endDateHint')}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('drydockCampaign.form.sectionShipyard')}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              name="shipyard"
              label={t('drydockCampaign.fields.shipyard')}
            />
            <TextField
              control={form.control}
              name="location"
              label={t('drydockCampaign.fields.location')}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('drydockCampaign.form.sectionBudget')}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              name="totalBudgetAmount"
              label={t('drydockCampaign.fields.totalBudget')}
              type="number"
            />
            <TextField
              control={form.control}
              name="totalBudgetCurrency"
              label="ISO 4217"
              placeholder={t('drydockCampaign.form.currencyPlaceholder')}
              hint={t('drydockCampaign.form.currencyHint')}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('drydockCampaign.form.sectionNotes')}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <TextField
              control={form.control}
              name="description"
              label={t('drydockCampaign.fields.description')}
              textarea
            />
            <TextField
              control={form.control}
              name="notes"
              label={t('drydockCampaign.fields.notes')}
              textarea
            />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pt-2">
          <Button asChild variant="ghost" disabled={submitting}>
            <Link to="/drydock-campaigns">{t('common.cancel')}</Link>
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              <Save aria-hidden />
            )}
            {mode === 'create'
              ? t('drydockCampaign.form.submitCreate')
              : t('drydockCampaign.form.submitUpdate')}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function CreateBranch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const createMut = useCreateDrydockCampaign();

  if (!user?.companyId) {
    return (
      <div className="space-y-4">
        <header className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon" aria-label={t('common.back')}>
            <Link to="/drydock-campaigns">
              <ArrowLeft aria-hidden />
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">
            {t('drydockCampaign.form.createTitle')}
          </h1>
        </header>
        <Alert>
          <AlertTitle>{t('drydockCampaign.list.noCompanyTitle')}</AlertTitle>
          <AlertDescription>
            {t('drydockCampaign.list.noCompanyHint')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const errorMessage = (() => {
    if (!createMut.error) return null;
    if (createMut.error instanceof ApiError) {
      return t(createMut.error.key, {
        defaultValue: t('drydockCampaign.form.saveError'),
        ...(createMut.error.params ?? {}),
      });
    }
    return t('drydockCampaign.form.saveError');
  })();

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon" aria-label={t('common.back')}>
          <Link to="/drydock-campaigns">
            <ArrowLeft aria-hidden />
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">
          {t('drydockCampaign.form.createTitle')}
        </h1>
      </header>

      <FormBody
        mode="create"
        initialValues={emptyDefaults()}
        submitting={createMut.isPending}
        errorMessage={errorMessage}
        onSubmit={(values) => {
          const payload: CreateDrydockCampaignInput = {
            companyId: user.companyId as string,
            vesselId: values.vesselId,
            title: values.title,
            description: values.description || undefined,
            startDate: values.startDate,
            endDate: values.endDate || undefined,
            location: values.location || undefined,
            shipyard: values.shipyard || undefined,
            totalBudgetAmount: values.totalBudgetAmount
              ? Number(values.totalBudgetAmount)
              : undefined,
            totalBudgetCurrency: values.totalBudgetCurrency
              ? values.totalBudgetCurrency.toUpperCase()
              : undefined,
            notes: values.notes || undefined,
          };
          createMut.mutate(payload, {
            onSuccess: (created) =>
              navigate(`/drydock-campaigns/${created.id}`),
          });
        }}
      />
    </div>
  );
}

function EditBranch({ id }: { id: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: existing, isLoading, isError, error } = useDrydockCampaign(id);
  const updateMut = useUpdateDrydockCampaign(id);

  const errorMessage = (() => {
    if (!updateMut.error) return null;
    if (updateMut.error instanceof ApiError) {
      return t(updateMut.error.key, {
        defaultValue: t('drydockCampaign.form.saveError'),
        ...(updateMut.error.params ?? {}),
      });
    }
    return t('drydockCampaign.form.saveError');
  })();

  const loadErrorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('drydockCampaign.detail.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('drydockCampaign.detail.loadError');
  })();

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon" aria-label={t('common.back')}>
          <Link to={`/drydock-campaigns/${id}`}>
            <ArrowLeft aria-hidden />
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">
          {t('drydockCampaign.form.editTitle')}
        </h1>
      </header>

      {loadErrorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('drydockCampaign.detail.loadError')}</AlertTitle>
          <AlertDescription>{loadErrorMessage}</AlertDescription>
        </Alert>
      )}

      {isLoading && <Skeleton className="h-96 w-full" />}

      {existing && (
        <FormBody
          mode="edit"
          initialValues={defaultsFromExisting(existing)}
          submitting={updateMut.isPending}
          errorMessage={errorMessage}
          onSubmit={(values) => {
            const payload: UpdateDrydockCampaignInput = {
              title: values.title,
              description: values.description || undefined,
              endDate: values.endDate || undefined,
              location: values.location || undefined,
              shipyard: values.shipyard || undefined,
              totalBudgetAmount: values.totalBudgetAmount
                ? Number(values.totalBudgetAmount)
                : undefined,
              totalBudgetCurrency: values.totalBudgetCurrency
                ? values.totalBudgetCurrency.toUpperCase()
                : undefined,
              notes: values.notes || undefined,
            };
            updateMut.mutate(payload, {
              onSuccess: (updated) =>
                navigate(`/drydock-campaigns/${updated.id}`),
            });
          }}
        />
      )}
    </div>
  );
}

export function DrydockCampaignFormPage() {
  const { id } = useParams<{ id?: string }>();
  return id ? <EditBranch id={id} /> : <CreateBranch />;
}
